import * as React from "react"
import { Dialog } from "radix-ui"
import { ChevronRightIcon, XIcon } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../../lib/utils"
import { useOverlayLock, guardWhenLocked, OVERLAY_LOCKED_CLASSES } from "../../../providers/overlay-lock-provider"

type SheetClosePosition = "left" | "right"
type SheetCloseIcon = "x" | "chevron-right"

const sheetClosePositionClasses: Record<SheetClosePosition, string> = {
  // 24px from the edge aligns with DESIGN.md drawer header padding
  left: "top-6 left-6",
  right: "top-6 right-6",
}

const Sheet = Dialog.Root
const SheetTrigger = Dialog.Trigger
const SheetClose = Dialog.Close
const SheetPortal = Dialog.Portal

/**
 * Sheet (Drawer) — DESIGN.md `drawer-right`:
 * - 720px width on desktop, full-width on mobile (<768px).
 * - White bg, single shadow tier.
 * - Header: 24px padding, 1px gray-200 bottom border.
 * - Body: 24px padding, scrollable.
 * - Footer: 16px padding, 1px gray-200 top border, gray-50 background.
 * - Scrim: rgba(0, 0, 0, 0.4) per DESIGN.md.
 */
function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof Dialog.Overlay>) {
  return (
    <Dialog.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-(--z-sheet) bg-black/40",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className,
      )}
      {...props}
    />
  )
}

const sheetVariants = cva(
  "fixed z-(--z-sheet) flex flex-col bg-background shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-200 data-[state=closed]:duration-200",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b border-border data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top",
        bottom: "inset-x-0 bottom-0 border-t border-border data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
        left: "inset-y-0 left-0 h-full w-full max-w-[720px] border-r border-border data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
        // DESIGN.md: 720px desktop, full-width mobile (<768px)
        right: "inset-y-0 right-0 h-full w-full max-w-[720px] border-l border-border data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
)

function SheetContent({
  side = "right",
  className,
  children,
  closePosition = "right",
  closeIcon = "x",
  closeClassName,
  showClose = true,
  inert,
  "aria-busy": ariaBusy,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof Dialog.Content> &
  VariantProps<typeof sheetVariants> & {
    closePosition?: SheetClosePosition
    closeIcon?: SheetCloseIcon
    /** Overrides the close control's position classes, e.g. to center it on a shorter header. */
    closeClassName?: string
    showClose?: boolean
  }) {
  const isLocked = useOverlayLock()
  const CloseIcon = closeIcon === "chevron-right" ? ChevronRightIcon : XIcon

  return (
    <SheetPortal>
      <SheetOverlay />
      <Dialog.Content
        data-slot="sheet-content"
        {...props}
        // Lock attrs after `{...props}` so consumers cannot re-enable a locked overlay.
        inert={isLocked || inert}
        aria-busy={isLocked || ariaBusy}
        onEscapeKeyDown={guardWhenLocked(isLocked, onEscapeKeyDown)}
        onInteractOutside={guardWhenLocked(isLocked, onInteractOutside)}
        className={cn(sheetVariants({ side }), className, isLocked && OVERLAY_LOCKED_CLASSES)}
      >
        {children}
        {showClose ? (
          <Dialog.Close
            className={cn(
              "absolute z-10 inline-flex size-7 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none",
              sheetClosePositionClasses[closePosition],
              closeClassName,
            )}
          >
            <CloseIcon className="size-4" />
            <span className="sr-only">Close</span>
          </Dialog.Close>
        ) : null}
      </Dialog.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1 border-b border-border px-6 py-6", className)}
      {...props}
    />
  )
}

function SheetBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-body"
      className={cn("flex-1 overflow-y-auto px-6 py-6", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex items-center gap-2 border-t border-border bg-muted px-4 py-4", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof Dialog.Title>) {
  return (
    <Dialog.Title
      data-slot="sheet-title"
      className={cn("text-[15px] font-normal text-foreground", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof Dialog.Description>) {
  return (
    <Dialog.Description
      data-slot="sheet-description"
      className={cn("text-xs text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
